import './style.css';
import type { AppState, AppView, CartItem, Enquiry, Order, Product } from './types';
import { CATEGORIES, priceForQty, priceForQtyNgn, formatMoney, formatMoneyNgn, FREIGHT_FLAT_NGN } from './data/products';
import { getCurrency, setCurrency } from './lib/currency';
import { getTheme, setTheme, initTheme } from './lib/theme';
import { backendLabel, isSupabaseConfigured } from './lib/supabase';
import {
  getCart,
  setCart,
  saveEnquiry,
  saveOrder,
  getLocalEnquiries,
  getLocalOrders,
  getLocalOrderById,
  updateOrderPayment,
  fetchRemoteEnquiries,
  fetchRemoteOrders,
  syncStorageScopeFromAuth,
} from './lib/store';
import {
  getActiveCatalog,
  getCatalogVersion,
  listSellerProducts,
  onCatalogChange,
  productById as catalogProductById,
  productImageHtml,
  refreshCatalog,
  saveSellerProduct,
  sellerFactoryName,
  type CatalogProduct,
} from './lib/catalog';
import {
  chargeWithPaystack,
  completeDemoPayment,
  isPaystackConfigured,
  paymentModeLabel,
  paymentStatusLabel,
} from './lib/payments';
import {
  initAuth,
  onAuthChange,
  isSignedIn,
  signIn,
  signUp,
  signOut,
  authState,
  displayName,
} from './lib/auth';
import {
  getOrCreateConversation,
  listConversationsForCurrentUser,
  listMessages,
  sendMessage,
  markConversationRead,
  subscribeToConversation,
  getConversationById,
  messagingAvailable,
  type ChatMessage,
  type Conversation,
} from './lib/messages';

const FREIGHT_FLAT = FREIGHT_FLAT_NGN; // NGN inland haulage estimate
const state: AppState = {
  view: 'home',
  category: 'All',
  search: '',
  sort: 'featured',
  productId: null,
  role: 'buyer',
  sellerTab: 'enquiries',
  conversationId: null,
  chatFactoryName: null,
  chatProductId: null,
  chatProductName: null,
  authTab: 'signin',
  authReturn: null,
  guestEnquire: false,
  guestCheckout: false,
};

/** Last order shown on the confirmation screen (payment retries). */
let lastOrderId: string | null = null;
let checkoutBusy = false;
let chatUnsub: (() => void) | null = null;
let sellerChatUnsub: (() => void) | null = null;
let chatSendBusy = false;

// Avoid rebuilding the catalog when navigation or price refreshes leave its inputs unchanged.
let homeRenderKey = '';

function $<T extends HTMLElement = HTMLElement>(sel: string, el: ParentNode = document): T {
  return el.querySelector(sel) as T;
}

function $$<T extends HTMLElement = HTMLElement>(sel: string, el: ParentNode = document): T[] {
  return [...el.querySelectorAll(sel)] as T[];
}

function eventEl(e: Event): Element | null {
  const t = e.target;
  return t instanceof Element ? t : null;
}

function inputValue(el: HTMLElement | null | undefined): string {
  return el && 'value' in el ? String((el as HTMLInputElement).value) : '';
}

let toastTimer: ReturnType<typeof setTimeout> | undefined;

function toast(msg: string): void {
  const el = $('#toast');
  if (!el) return;
  el.textContent = msg;
  el.hidden = false;
  clearTimeout(toastTimer);
  toastTimer = setTimeout(() => {
    el.hidden = true;
  }, 2800);
}

function productById(id: string | null | undefined): CatalogProduct | undefined {
  return catalogProductById(id);
}

function filteredProducts(): CatalogProduct[] {
  let list = [...getActiveCatalog()];
  if (state.category !== 'All') {
    list = list.filter((p) => p.category === state.category);
  }
  const q = state.search.trim().toLowerCase();
  if (q) {
    list = list.filter(
      (p) =>
        p.name.toLowerCase().includes(q) ||
        p.factory.toLowerCase().includes(q) ||
        p.sku.toLowerCase().includes(q) ||
        p.category.toLowerCase().includes(q),
    );
  }
  switch (state.sort) {
    case 'price-asc':
      list.sort((a, b) => a.unitPrice - b.unitPrice);
      break;
    case 'price-desc':
      list.sort((a, b) => b.unitPrice - a.unitPrice);
      break;
    case 'moq':
      list.sort((a, b) => a.moq - b.moq);
      break;
    case 'lead':
      list.sort((a, b) => a.leadTimeDays - b.leadTimeDays);
      break;
    default:
      list.sort((a, b) => Number(b.featured) - Number(a.featured) || a.name.localeCompare(b.name));
  }
  return list;
}

/** Number of distinct cart line items (not summed MOQ qty — that leaked into the badge). */
function cartCount(): number {
  return getCart().length;
}

function formatBadgeCount(n: number): string {
  if (n > 99) return '99+';
  return String(n);
}

function updateBadges(): void {
  const n = cartCount();
  for (const id of ['cart-badge', 'cart-badge-mobile']) {
    const el = $('#' + id);
    if (!el) continue;
    el.hidden = n === 0;
    el.textContent = formatBadgeCount(n);
  }
}

function syncCurrencySelect(): void {
  const sel = $<HTMLSelectElement>('#currency-select');
  if (sel) sel.value = getCurrency();
}

function refreshPrices(): void {
  syncCurrencySelect();
  const name = state.view;
  if (name === 'home') renderHome();
  else if (name === 'detail') renderDetail();
  else if (name === 'cart') renderCart();
  else if (name === 'checkout') renderCheckout();
  else if (name === 'orders') renderBuyerOrders();
  else if (name === 'seller') renderSeller();
  else if (name === 'order-done') {
    renderOrderDone();
  }
  updateBadges();
}

function showView(name: AppView | string): void {
  state.view = name as AppView;
  $$('.view').forEach((v) => {
    v.hidden = v.id !== `view-${name}`;
  });
  $$('.nav-link').forEach((btn) => {
    btn.classList.toggle('active', btn.dataset.nav === name || (name === 'detail' && btn.dataset.nav === 'home') || ((name === 'enquire' || name === 'enquire-done') && btn.dataset.nav === 'home') || ((name === 'checkout' || name === 'order-done') && btn.dataset.nav === 'cart') || (name === 'auth' && btn.dataset.nav === 'auth'));
  });
  closeDrawer();
  window.scrollTo(0, 0);

  if (name !== 'chat') {
    stopChatSubscription();
  }
  if (name !== 'seller') {
    stopSellerChatSubscription();
  }
  if (name === 'home') renderHome();
  if (name === 'detail') renderDetail();
  if (name === 'cart') renderCart();
  if (name === 'checkout') renderCheckout();
  if (name === 'orders') renderBuyerOrders();
  if (name === 'seller') renderSeller();
  if (name === 'auth') renderAuth();
  if (name === 'enquire') syncEnquireGate();
  if (name === 'order-done') renderOrderDone();
  if (name === 'chat') void renderChat();
  if (name === 'enquire-done') syncEnquireDoneMessageBtn();
  updateBadges();
  updateAuthChip();
}

function closeDrawer(): void {
  const drawer = $('#mobile-drawer');
  const overlay = $('#drawer-overlay');
  const toggle = $('#menu-toggle');
  if (drawer) drawer.hidden = true;
  if (overlay) overlay.hidden = true;
  if (toggle) {
    toggle.setAttribute('aria-expanded', 'false');
    toggle.setAttribute('aria-label', 'Open menu');
  }
  document.body.classList.remove('drawer-open');
}

function openDrawer(): void {
  const drawer = $('#mobile-drawer');
  const overlay = $('#drawer-overlay');
  const toggle = $('#menu-toggle');
  if (drawer) drawer.hidden = false;
  if (overlay) overlay.hidden = false;
  if (toggle) {
    toggle.setAttribute('aria-expanded', 'true');
    toggle.setAttribute('aria-label', 'Close menu');
  }
  document.body.classList.add('drawer-open');
}

function renderCategoryPills(): void {
  const host = $('#category-pills');
  const pills = $$('.pill', host);
  if (pills.length !== CATEGORIES.length) {
    host.innerHTML = CATEGORIES.map(
      (c) =>
        `<button type="button" class="pill" role="tab" aria-selected="false" data-category="${c}">${c}</button>`,
    ).join('');
  }
  $$('.pill', host).forEach((pill) => {
    const active = pill.dataset.category === state.category;
    pill.classList.toggle('active', active);
    pill.setAttribute('aria-selected', String(active));
  });
}

function renderHome(): void {
  const renderKey = `${state.category}\u0000${state.search}\u0000${state.sort}\u0000${getCurrency()}\u0000${getCatalogVersion()}`;
  renderCategoryPills();
  if (renderKey === homeRenderKey) return;
  homeRenderKey = renderKey;
  const list = filteredProducts();
  $('#results-count').textContent = `${list.length} product${list.length === 1 ? '' : 's'} · ${backendLabel()}`;
  const grid = $('#product-grid');
  const empty = $('#home-empty');
  if (!list.length) {
    grid.innerHTML = '';
    empty.hidden = false;
    return;
  }
  empty.hidden = true;
  grid.innerHTML = list
    .map(
      (p) => `
    <button type="button" class="product-card" role="listitem" data-product-id="${p.id}" aria-label="${escapeAttr(p.name)} from ${escapeAttr(p.factory)}">
      <div class="card-visual" data-category="${escapeAttr(p.category)}" aria-hidden="true">
        <span class="card-cat">${escapeHtml(p.category)}</span>
        ${productImageHtml(p, { className: 'product-image', size: 'card' })}
      </div>
      <div class="card-body">
        <h2>${escapeHtml(p.name)}</h2>
        <p class="card-factory">${escapeHtml(p.factory)}</p>
        <div class="card-meta">
          <div class="meta-item price"><span>Wholesale</span><strong>${formatMoneyNgn(priceForQtyNgn(p, p.moq))}</strong></div>
          <div class="meta-item"><span>MOQ</span><strong>${p.moq.toLocaleString()} ${p.unit}</strong></div>
          <div class="meta-item"><span>Lead time</span><strong>${p.leadTimeDays} days</strong></div>
          <div class="meta-item"><span>SKU</span><strong>${escapeHtml(p.sku)}</strong></div>
        </div>
      </div>
    </button>`,
    )
    .join('');
}

function renderDetail(): void {
  const p = productById(state.productId);
  const host = $('#detail-content');
  if (!p) {
    host.innerHTML = `<p>Product not found.</p><button type="button" class="btn btn-primary" data-nav="home">Back to browse</button>`;
    return;
  }
  const tierSource = p.priceTiersNgn?.length
    ? p.priceTiersNgn.map((t) => ({ minQty: t.minQty, label: formatMoneyNgn(t.price) }))
    : (p.priceTiers || []).map((t) => ({ minQty: t.minQty, label: formatMoney(t.price) }));
  const tiers = tierSource
    .map(
      (t) =>
        `<tr><td>${t.minQty.toLocaleString()}+ ${escapeHtml(p.unit)}</td><td>${t.label} / ${escapeHtml(p.unit)}</td></tr>`,
    )
    .join('');
  const specs = Object.entries(p.specs || {})
    .map(([k, v]) => `<li><span>${escapeHtml(k)}</span><strong>${escapeHtml(String(v))}</strong></li>`)
    .join('');

  host.innerHTML = `
    <div class="detail-visual" data-category="${escapeAttr(p.category)}" aria-hidden="true">
      ${productImageHtml(p, { className: 'product-image', size: 'detail' })}
    </div>
    <div class="detail-info">
      <p class="eyebrow">${escapeHtml(p.category)}</p>
      <h1 id="detail-heading">${escapeHtml(p.name)}</h1>
      <p class="detail-factory">${escapeHtml(p.factory)} · SKU ${escapeHtml(p.sku)}</p>
      <div class="detail-chips">
        <span class="chip">MOQ <strong>${p.moq.toLocaleString()} ${escapeHtml(p.unit)}</strong></span>
        <span class="chip">From <strong>${formatMoneyNgn(priceForQtyNgn(p, p.moq))}</strong></span>
        <span class="chip">Lead <strong>${p.leadTimeDays} days</strong></span>
      </div>
      <p class="detail-desc">${escapeHtml(p.description)}</p>
      <h2 class="form-section-title" style="margin:0 0 0.5rem;font-size:0.95rem">Specifications</h2>
      <ul class="specs">${specs}</ul>
      <div class="tiers">
        <h2>Price tiers</h2>
        <table class="tier-table">
          <thead><tr><th>Quantity</th><th>Unit price</th></tr></thead>
          <tbody>${tiers}</tbody>
        </table>
      </div>
      <div class="qty-row">
        <div class="field">
          <label for="detail-qty">Order quantity</label>
          <input type="number" id="detail-qty" min="${p.moq}" step="1" value="${p.moq}" />
          <p class="field-hint">Minimum order quantity: ${p.moq.toLocaleString()} ${escapeHtml(p.unit)}</p>
        </div>
      </div>
      <div class="detail-actions">
        <button type="button" class="btn btn-primary" id="btn-add-cart">Add to cart</button>
        <button type="button" class="btn btn-secondary" id="btn-enquire">Enquire</button>
        <button type="button" class="btn btn-ghost" id="btn-message-seller">Message seller</button>
      </div>
    </div>`;

  $('#btn-add-cart').addEventListener('click', () => {
    const qty = Math.max(1, parseInt($<HTMLInputElement>('#detail-qty').value, 10) || p.moq);
    if (qty < p.moq) {
      toast(`Quantity must meet MOQ of ${p.moq.toLocaleString()}`);
      $<HTMLInputElement>('#detail-qty').focus();
      return;
    }
    addToCart(p.id, qty);
    toast('Added to cart');
    updateBadges();
  });
  $('#btn-enquire').addEventListener('click', () => openEnquire(p.id));
  $('#btn-message-seller')?.addEventListener('click', () => {
    void openMessageSeller({
      factoryName: p.factory,
      productId: p.id,
      productName: p.name,
    });
  });
}

function openEnquire(productId: string): void {
  const p = productById(productId);
  if (!p) return;
  state.productId = productId;
  state.guestEnquire = false;
  $<HTMLInputElement>('#enquire-product-id').value = p.id;
  $('#enquire-product-summary').textContent = `${p.name} · ${p.factory} · MOQ ${p.moq.toLocaleString()} ${p.unit}`;
  $<HTMLInputElement>('#enq-qty').value = String(p.moq);
  $<HTMLInputElement>('#enq-qty').min = '1';
  $('#enq-moq-hint').textContent = `Factory MOQ is ${p.moq.toLocaleString()} ${p.unit}. You may enquire for other volumes.`;
  if (isSignedIn() && authState.profile?.company && !$<HTMLInputElement>('#enq-company').value) {
    $<HTMLInputElement>('#enq-company').value = authState.profile.company;
  }
  if (isSignedIn() && authState.user?.email && !$<HTMLInputElement>('#enq-email').value) {
    $<HTMLInputElement>('#enq-email').value = authState.user.email;
  }
  $<HTMLButtonElement>('#enquire-back').onclick = () => {
    state.productId = p.id;
    showView('detail');
  };
  showView('enquire');
}

function syncEnquireGate(): void {
  const gate = $('#enquire-auth-gate');
  const form = $('#enquire-form');
  if (!gate || !form) return;
  const needPrompt = isSupabaseConfigured && !isSignedIn() && !state.guestEnquire;
  gate.hidden = !needPrompt;
  form.hidden = needPrompt;
}

function syncCheckoutGate(): void {
  const gate = $('#checkout-auth-gate');
  const form = $('#checkout-form');
  if (!gate || !form) return;
  const needPrompt = isSupabaseConfigured && !isSignedIn() && !state.guestCheckout;
  gate.hidden = !needPrompt;
  form.hidden = needPrompt;
  const guestChip = $('#checkout-guest-chip');
  if (guestChip) {
    guestChip.hidden = needPrompt || isSignedIn();
    if (!guestChip.hidden) {
      guestChip.textContent = state.guestCheckout ? 'Checking out as guest' : 'Local demo checkout';
    }
  }
}

function addToCart(productId: string, qty: number): void {
  const cart = getCart();
  const existing = cart.find((i) => i.productId === productId);
  if (existing) existing.qty = qty;
  else cart.push({ productId, qty });
  setCart(cart);
  updateBadges();
}

function setCartQty(productId: string, qty: number): void {
  const cart = getCart();
  const item = cart.find((i) => i.productId === productId);
  if (!item) return;
  item.qty = Math.max(1, Math.floor(qty) || 1);
  setCart(cart);
  renderCart();
  updateBadges();
}

function cartTotals(): { merchandise: number; freight: number; total: number; moqOk: boolean; lineCount: number } {
  const cart = getCart();
  let merchandise = 0;
  let moqOk = true;
  let lineCount = 0;
  for (const item of cart) {
    const p = productById(item.productId);
    if (!p) continue;
    lineCount += 1;
    if (item.qty < p.moq) moqOk = false;
    merchandise += priceForQtyNgn(p, item.qty) * item.qty;
  }
  const freight = lineCount ? FREIGHT_FLAT : 0;
  return { merchandise, freight, total: merchandise + freight, moqOk, lineCount };
}

function clearFieldErrors(prefix: string, ids: string[]): void {
  for (const id of ids) {
    const field = $(`#${prefix}${id}`)?.closest('.field');
    if (field) field.classList.remove('is-invalid');
    const err = $(`#${prefix}${id}-error`);
    if (err) {
      err.hidden = true;
      err.textContent = '';
    }
  }
  const formErr = $('#checkout-form-error');
  if (formErr) {
    formErr.hidden = true;
    formErr.textContent = '';
  }
}

function setFieldError(inputId: string, message: string): void {
  const input = $(`#${inputId}`);
  const field = input?.closest('.field');
  if (field) field.classList.add('is-invalid');
  const err = $(`#${inputId}-error`);
  if (err) {
    err.textContent = message;
    err.hidden = false;
  }
}

function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

function isValidPhone(phone: string): boolean {
  if (!phone) return true;
  const digits = phone.replace(/\D/g, '');
  return digits.length >= 7 && digits.length <= 15;
}

function renderCart(): void {
  const cart = getCart();
  const empty = $('#cart-empty');
  const content = $('#cart-content');
  const lead = $('#cart-lead');
  if (lead) lead.hidden = !cart.length;
  if (!cart.length) {
    empty.hidden = false;
    content.hidden = true;
    return;
  }
  empty.hidden = true;
  content.hidden = false;

  const { merchandise, freight, total, moqOk, lineCount } = cartTotals();
  const lines = $('#cart-lines');
  lines.innerHTML = cart
    .map((item) => {
      const p = productById(item.productId);
      if (!p) return '';
      const unit = priceForQtyNgn(p, item.qty);
      const line = unit * item.qty;
      const below = item.qty < p.moq;
      const atMoq = item.qty <= p.moq;
      return `
      <article class="cart-line" role="listitem" data-cart-id="${p.id}">
        <div class="cart-line-top">
          <div class="cart-line-identity">
            ${productImageHtml(p, { className: 'product-image', size: 'thumb' })}
            <div>
              <h3>${escapeHtml(p.name)}</h3>
              <p>${escapeHtml(p.factory)} · ${escapeHtml(p.sku)}</p>
            </div>
          </div>
          <button type="button" class="btn btn-danger-ghost" data-remove="${p.id}">Remove</button>
        </div>
        <div class="cart-line-controls">
          <div class="qty-control">
            <label for="qty-${p.id}">Qty (${escapeHtml(p.unit)})</label>
            <div class="qty-stepper">
              <button type="button" data-qty-step="-1" data-qty-id="${p.id}" aria-label="Decrease quantity" ${atMoq ? 'disabled' : ''}>−</button>
              <input type="number" id="qty-${p.id}" data-qty="${p.id}" min="${p.moq}" step="1" value="${item.qty}" inputmode="numeric" />
              <button type="button" data-qty-step="1" data-qty-id="${p.id}" aria-label="Increase quantity">+</button>
            </div>
          </div>
          <div class="line-total">${formatMoneyNgn(line)}</div>
          ${below ? `<p class="moq-warn">Below MOQ (${p.moq.toLocaleString('en-GB')} ${escapeHtml(p.unit)}). Increase quantity to continue.</p>` : `<p class="field-hint" style="width:100%;margin:0">Unit ${formatMoneyNgn(unit)} · MOQ ${p.moq.toLocaleString('en-GB')}</p>`}
        </div>
      </article>`;
    })
    .join('');

  const lineCountEl = $('#cart-line-count');
  if (lineCountEl) lineCountEl.textContent = String(lineCount);
  $('#cart-subtotal').textContent = formatMoneyNgn(merchandise);
  const freightEl = $('#cart-freight');
  if (freightEl) freightEl.textContent = formatMoneyNgn(freight);
  const totalEl = $('#cart-total');
  if (totalEl) totalEl.textContent = formatMoneyNgn(total);

  const moqHint = $('#cart-moq-hint');
  if (moqHint) {
    moqHint.textContent = moqOk
      ? 'All lines meet MOQ. Inland haulage is estimated at checkout (ex-duty).'
      : 'Fix quantities below MOQ before checkout.';
  }
  const cartErr = $('#cart-error');
  if (cartErr) {
    cartErr.hidden = moqOk;
    cartErr.textContent = moqOk ? '' : 'One or more lines are below the factory MOQ.';
  }

  const checkoutBtn = ($('#cart-checkout-btn') || content.querySelector('[data-nav="checkout"]')) as HTMLButtonElement | null;
  if (checkoutBtn) {
    checkoutBtn.disabled = !moqOk;
    checkoutBtn.title = moqOk ? '' : 'Fix quantities to meet MOQ before checkout';
  }
}

function renderCheckout(): void {
  const cart = getCart();
  if (!cart.length) {
    showView('cart');
    return;
  }
  const { merchandise, freight, total, moqOk } = cartTotals();
  if (!moqOk) {
    toast('Adjust cart quantities to meet MOQ');
    showView('cart');
    return;
  }

  const linesHost = $('#checkout-lines');
  if (linesHost) {
    linesHost.innerHTML = cart
      .map((item) => {
        const p = productById(item.productId);
        if (!p) return '';
        const unit = priceForQtyNgn(p, item.qty);
        const line = unit * item.qty;
        return `<div class="checkout-line">
          <div>
            <strong>${escapeHtml(p.name)}</strong>
            <div class="meta">${escapeHtml(p.factory)} · Qty ${item.qty.toLocaleString('en-GB')} ${escapeHtml(p.unit)} · ${formatMoneyNgn(unit)} each</div>
          </div>
          <div class="amt">${formatMoneyNgn(line)}</div>
        </div>`;
      })
      .join('');
  }

  $('#co-merchandise').textContent = formatMoneyNgn(merchandise);
  $('#co-freight').textContent = formatMoneyNgn(freight);
  $('#co-total').textContent = formatMoneyNgn(total);

  const modeEl = $('#checkout-payment-mode');
  if (modeEl) modeEl.textContent = `Mode: ${paymentModeLabel()}`;
  const noteEl = $('#checkout-payment-note');
  if (noteEl) {
    noteEl.textContent = isPaystackConfigured()
      ? 'Paystack Inline will open for NGN payment after you confirm details.'
      : 'No Paystack public key set — you can place the order and simulate payment on the confirmation screen.';
  }
  const submitBtn = $('#checkout-submit') as HTMLButtonElement | null;
  if (submitBtn) {
    submitBtn.textContent = isPaystackConfigured() ? 'Pay & place order' : 'Place order (demo pay next)';
    submitBtn.disabled = checkoutBusy;
  }

  const guestChip = $('#checkout-guest-chip');
  if (guestChip) {
    const showGuest = !isSignedIn() && (state.guestCheckout || !isSupabaseConfigured);
    guestChip.hidden = !showGuest;
    guestChip.textContent = isSignedIn()
      ? ''
      : isSupabaseConfigured
        ? 'Checking out as guest'
        : 'Local demo checkout (Supabase optional)';
  }

  if (isSignedIn()) {
    if (authState.profile?.company && !$<HTMLInputElement>('#co-company').value) {
      $<HTMLInputElement>('#co-company').value = authState.profile.company;
    }
    if (authState.user?.email && !$<HTMLInputElement>('#co-email').value) {
      $<HTMLInputElement>('#co-email').value = authState.user.email;
    }
  }
  syncCheckoutGate();
}

function renderOrderDone(): void {
  const order = lastOrderId ? getLocalOrderById(lastOrderId) : getLocalOrders()[0];
  if (!order) {
    $('#order-done-heading').textContent = 'Order placed';
    $('#order-done-body').textContent = 'Your order was recorded.';
    return;
  }
  const paid = order.paymentStatus === 'paid';
  $('#order-done-heading').textContent = paid ? 'Order confirmed' : 'Order placed — payment pending';
  const badge = $('#order-payment-badge');
  if (badge) {
    badge.textContent = paymentStatusLabel(order.paymentStatus);
    badge.className = `payment-badge is-${order.paymentStatus || 'pending'}`;
  }
  const icon = $('#order-done-icon');
  if (icon) icon.textContent = paid ? '✓' : '…';
  $('#order-id-code').textContent = order.id;
  const itemSummary = (order.items || [])
    .map((it) => `${it.name} × ${it.qty.toLocaleString('en-GB')}`)
    .join('; ');
  $('#order-done-body').textContent = paid
    ? `Thank you, ${order.contact}. Payment received for ${formatMoneyNgn(order.total)} (${order.company}). ${itemSummary ? `Items: ${itemSummary}.` : ''} We will coordinate delivery to your ship-to address.`
    : `Thank you, ${order.contact}. Your wholesale order for ${formatMoneyNgn(order.total)} is saved for ${order.company}, but payment is still outstanding. Complete payment to confirm fulfilment.`;
  const refEl = $('#order-payment-ref');
  if (refEl) {
    if (order.paymentRef) {
      refEl.hidden = false;
      refEl.textContent = `Payment reference: ${order.paymentRef}`;
    } else {
      refEl.hidden = true;
      refEl.textContent = '';
    }
  }
  const payNow = $('#order-pay-now') as HTMLButtonElement | null;
  const demoPay = $('#order-demo-pay') as HTMLButtonElement | null;
  if (payNow) payNow.hidden = paid || !isPaystackConfigured();
  if (demoPay) demoPay.hidden = paid || isPaystackConfigured();
}

async function renderBuyerOrders(): Promise<void> {
  const orders = getLocalOrders();
  const host = $('#orders-list');
  const empty = $('#orders-empty');
  if (!orders.length) {
    host.innerHTML = '';
    empty.hidden = false;
    return;
  }
  empty.hidden = true;
  host.innerHTML = orders.map(orderCardHtml).join('');
}

async function renderSeller(): Promise<void> {
  const factory = sellerFactoryName();
  await refreshCatalog({ includeInactiveForFactory: factory }).catch(() => undefined);

  const enquiries = await fetchRemoteEnquiries().catch(() => getLocalEnquiries());
  const orders = await fetchRemoteOrders().catch(() => getLocalOrders());
  const catalogue = listSellerProducts();

  const enqBadge = $('#enq-count-badge');
  const ordBadge = $('#ord-count-badge');
  const catBadge = $('#cat-count-badge');
  enqBadge.hidden = !enquiries.length;
  enqBadge.textContent = String(enquiries.length);
  ordBadge.hidden = !orders.length;
  ordBadge.textContent = String(orders.length);
  if (catBadge) {
    catBadge.hidden = !catalogue.length;
    catBadge.textContent = String(catalogue.length);
  }
  // Soft badge for unread buyer messages (best-effort; full list on Messages tab)
  void listConversationsForCurrentUser()
    .then((convs) => {
      const badge = $('#msg-count-badge');
      if (!badge) return;
      const unread = convs.reduce((n, c) => n + (c.unreadCount || 0), 0);
      badge.hidden = unread === 0;
      badge.textContent = String(unread > 99 ? '99+' : unread);
    })
    .catch(() => undefined);

  const tab =
    state.sellerTab === 'orders' ||
    state.sellerTab === 'catalogue' ||
    state.sellerTab === 'messages'
      ? state.sellerTab
      : 'enquiries';
  state.sellerTab = tab;
  $('#seller-enquiries').hidden = tab !== 'enquiries';
  $('#seller-orders').hidden = tab !== 'orders';
  const catPanel = $('#seller-catalogue');
  if (catPanel) catPanel.hidden = tab !== 'catalogue';
  const msgPanel = $('#seller-messages');
  if (msgPanel) msgPanel.hidden = tab !== 'messages';
  $$('.seller-tab').forEach((t) => {
    // Only toggle desk tabs (not auth tabs sharing .seller-tab)
    if (!t.dataset.sellerTab) return;
    const on = t.dataset.sellerTab === tab;
    t.classList.toggle('active', on);
    t.setAttribute('aria-selected', String(on));
  });

  const enqList = $('#seller-enq-list');
  const enqEmpty = $('#seller-enq-empty');
  if (!enquiries.length) {
    enqList.innerHTML = '';
    enqEmpty.hidden = false;
  } else {
    enqEmpty.hidden = true;
    enqList.innerHTML = enquiries
      .map(
        (e) => `
      <article class="data-card">
        <header>
          <h3>${escapeHtml(e.productName)}</h3>
          <span class="status">${escapeHtml(e.status || 'new')}</span>
        </header>
        <p class="meta">${formatDate(e.createdAt)} · ${escapeHtml(e.id?.slice?.(0, 8) || '')}</p>
        <dl>
          <div><dt>Factory</dt><dd>${escapeHtml(e.factoryName || '—')}</dd></div>
          <div><dt>Company</dt><dd>${escapeHtml(e.company)}</dd></div>
          <div><dt>Contact</dt><dd>${escapeHtml(e.contact)} · ${escapeHtml(e.email)}</dd></div>
          <div><dt>Quantity</dt><dd>${Number(e.quantity).toLocaleString()}</dd></div>
        </dl>
        <div class="message-block">${escapeHtml(e.message)}</div>
      </article>`,
      )
      .join('');
  }

  const ordList = $('#seller-ord-list');
  const ordEmpty = $('#seller-ord-empty');
  if (!orders.length) {
    ordList.innerHTML = '';
    ordEmpty.hidden = false;
  } else {
    ordEmpty.hidden = true;
    ordList.innerHTML = orders.map(orderCardHtml).join('');
  }

  if (tab === 'catalogue') renderSellerCatalogue(catalogue, factory);
  if (tab === 'messages') await renderSellerMessages();
  else stopSellerChatSubscription();
}

function renderSellerCatalogue(catalogue: CatalogProduct[], factory: string | null): void {
  const hint = $('#catalogue-factory-hint');
  if (hint) {
    hint.textContent = factory
      ? `Factory: ${factory}. Buyers see active items only.`
      : 'Sign in as Factory with a company name to sync products to Supabase. You can still draft items locally.';
  }
  const list = $('#seller-cat-list');
  const empty = $('#seller-cat-empty');
  if (!list || !empty) return;
  if (!catalogue.length) {
    list.innerHTML = '';
    empty.hidden = false;
  } else {
    empty.hidden = true;
    list.innerHTML = catalogue
      .map((p) => {
        const active = p.active !== false;
        return `
      <article class="data-card catalogue-card">
        <div class="catalogue-card-media">
          ${productImageHtml(p, { className: 'product-image', size: 'thumb' })}
        </div>
        <div class="catalogue-card-body">
          <header>
            <h3>${escapeHtml(p.name)}</h3>
            <span class="status ${active ? 'is-paid' : ''}">${active ? 'Active' : 'Inactive'}</span>
          </header>
          <p class="meta">${escapeHtml(p.category)} · SKU ${escapeHtml(p.sku)}${p.localOnly ? ' · local' : ''}</p>
          <dl>
            <div><dt>MOQ</dt><dd>${p.moq.toLocaleString('en-GB')} ${escapeHtml(p.unit)}</dd></div>
            <div><dt>Unit price</dt><dd>${formatMoneyNgn(p.unitPriceNgn ?? priceForQtyNgn(p, p.moq))}</dd></div>
          </dl>
          <div class="form-actions catalogue-card-actions">
            <button type="button" class="btn btn-secondary" data-catalogue-edit="${escapeAttr(p.id)}">Edit</button>
          </div>
        </div>
      </article>`;
      })
      .join('');
  }
}

let catalogueBusy = false;
let cataloguePendingFile: File | null = null;

function resetCatalogueForm(): void {
  const form = $<HTMLFormElement>('#catalogue-form');
  if (!form) return;
  form.hidden = true;
  form.reset();
  $<HTMLInputElement>('#cat-id').value = '';
  $<HTMLInputElement>('#cat-active').checked = true;
  cataloguePendingFile = null;
  const preview = $('#cat-image-preview');
  if (preview) {
    preview.hidden = true;
    preview.innerHTML = '';
  }
  const err = $('#catalogue-form-error');
  if (err) {
    err.hidden = true;
    err.textContent = '';
  }
  $('#catalogue-form-title').textContent = 'Add product';
  const saveBtn = $('#catalogue-save-btn') as HTMLButtonElement | null;
  if (saveBtn) {
    saveBtn.disabled = false;
    saveBtn.textContent = 'Save product';
  }
}

function openCatalogueForm(product?: CatalogProduct | null): void {
  const form = $<HTMLFormElement>('#catalogue-form');
  if (!form) return;
  form.hidden = false;
  cataloguePendingFile = null;
  const preview = $('#cat-image-preview');
  if (product) {
    $('#catalogue-form-title').textContent = 'Edit product';
    $<HTMLInputElement>('#cat-id').value = product.id;
    $<HTMLInputElement>('#cat-name').value = product.name;
    $<HTMLInputElement>('#cat-moq').value = String(product.moq);
    $<HTMLInputElement>('#cat-price').value = String(
      Math.round(product.unitPriceNgn ?? priceForQtyNgn(product, product.moq)),
    );
    $<HTMLSelectElement>('#cat-category').value = product.category;
    $<HTMLTextAreaElement>('#cat-desc').value = product.description || '';
    $<HTMLInputElement>('#cat-active').checked = product.active !== false;
    if (preview) {
      if (product.imageUrl) {
        preview.hidden = false;
        preview.innerHTML = `<img src="${escapeAttr(product.imageUrl)}" alt="" />`;
      } else {
        preview.hidden = true;
        preview.innerHTML = '';
      }
    }
  } else {
    $('#catalogue-form-title').textContent = 'Add product';
    $<HTMLInputElement>('#cat-id').value = '';
    $<HTMLInputElement>('#cat-name').value = '';
    $<HTMLInputElement>('#cat-moq').value = '100';
    $<HTMLInputElement>('#cat-price').value = '';
    $<HTMLSelectElement>('#cat-category').value = 'Industrial Parts';
    $<HTMLTextAreaElement>('#cat-desc').value = '';
    $<HTMLInputElement>('#cat-active').checked = true;
    $<HTMLInputElement>('#cat-image').value = '';
    if (preview) {
      preview.hidden = true;
      preview.innerHTML = '';
    }
  }
  const err = $('#catalogue-form-error');
  if (err) {
    err.hidden = true;
    err.textContent = '';
  }
  form.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  $<HTMLInputElement>('#cat-name').focus();
}

function orderCardHtml(o: Order): string {
  const items = (o.items || [])
    .map((it) => `${escapeHtml(it.name)} × ${it.qty.toLocaleString('en-GB')}`)
    .join('; ');
  const pay = o.paymentStatus || 'pending';
  return `
    <article class="data-card">
      <header>
        <h3>${escapeHtml(o.id)}</h3>
        <span class="status is-${escapeAttr(pay)}">${escapeHtml(paymentStatusLabel(pay))}</span>
      </header>
      <p class="meta">${formatDate(o.createdAt)} · Fulfilment: ${escapeHtml(o.status || 'placed')}</p>
      <dl>
        <div><dt>Buyer</dt><dd>${escapeHtml(o.company)} · ${escapeHtml(o.contact)}</dd></div>
        <div><dt>Email</dt><dd>${escapeHtml(o.email)}</dd></div>
        <div><dt>Ship to</dt><dd>${escapeHtml(o.address || '—')}</dd></div>
        <div><dt>Items</dt><dd>${items || '—'}</dd></div>
        <div><dt>Total</dt><dd>${formatMoneyNgn(o.total || 0)}</dd></div>
        ${o.paymentRef ? `<div><dt>Payment ref</dt><dd>${escapeHtml(o.paymentRef)}</dd></div>` : ''}
        ${o.notes ? `<div><dt>Notes</dt><dd>${escapeHtml(o.notes)}</dd></div>` : ''}
      </dl>
    </article>`;
}


function updateAuthChip(): void {
  const chip = $('#auth-chip');
  const drawer = $('#drawer-auth');
  const label = isSignedIn() ? displayName() || 'Account' : 'Sign in';
  if (chip) chip.textContent = label;
  if (drawer) drawer.textContent = isSignedIn() ? 'Account' : 'Sign in';
}

function renderAuth(): void {
  const signedIn = $('#auth-signed-in');
  const signedOut = $('#auth-signed-out');
  const lead = $('#auth-lead');
  if (!isSupabaseConfigured) {
    if (lead) {
      lead.textContent =
        'Supabase is not configured. Add VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY to enable accounts.';
    }
    if (signedIn) signedIn.hidden = true;
    if (signedOut) signedOut.hidden = true;
    return;
  }
  if (isSignedIn()) {
    if (lead) lead.textContent = 'You are signed in. Enquiries and orders sync with your account.';
    if (signedIn) signedIn.hidden = false;
    if (signedOut) signedOut.hidden = true;
    $('#auth-email').textContent = authState.user?.email || '—';
    $('#auth-role').textContent = authState.profile?.role || authState.user?.user_metadata?.role || 'buyer';
    $('#auth-company').textContent = authState.profile?.company || '—';
  } else {
    if (lead) {
      lead.textContent = state.authReturn
        ? `Sign in to continue to ${state.authReturn}, or create an account.`
        : 'Sign in or create a THEG MARKET PLACE account to sync enquiries and orders.';
    }
    if (signedIn) signedIn.hidden = true;
    if (signedOut) signedOut.hidden = false;
    const showSignIn = state.authTab !== 'signup';
    $('#signin-form').hidden = !showSignIn;
    $('#signup-form').hidden = showSignIn;
    $$('[data-auth-tab]').forEach((t) => {
      const on = t.dataset.authTab === (showSignIn ? 'signin' : 'signup');
      t.classList.toggle('active', on);
      t.setAttribute('aria-selected', String(on));
    });
  }
}

function afterAuthSuccess(): void {
  const ret = state.authReturn;
  state.authReturn = null;
  state.guestEnquire = false;
  state.guestCheckout = false;
  updateAuthChip();
  if (ret === 'enquire' && state.productId) {
    openEnquire(state.productId);
    return;
  }
  if (ret === 'checkout') {
    showView('checkout');
    return;
  }
  if (ret === 'chat') {
    if (state.chatFactoryName) {
      void openMessageSeller({
        factoryName: state.chatFactoryName,
        productId: state.chatProductId,
        productName: state.chatProductName,
      });
    } else {
      showView('chat');
    }
    return;
  }
  if (authState.profile?.role === 'factory') {
    state.role = 'seller';
    syncRoleUI();
    showView('seller');
    return;
  }
  showView('home');
}

function formatDate(iso: string | undefined | null): string {
  if (!iso) return '';
  try {
    return new Intl.DateTimeFormat('en-GB', {
      dateStyle: 'medium',
      timeStyle: 'short',
      timeZone: 'Africa/Lagos',
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

function escapeHtml(str: unknown): string {
  return String(str ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function escapeAttr(str: unknown): string {
  return escapeHtml(str).replace(/'/g, '&#39;');
}

function stopChatSubscription(): void {
  if (chatUnsub) {
    chatUnsub();
    chatUnsub = null;
  }
}

function stopSellerChatSubscription(): void {
  if (sellerChatUnsub) {
    sellerChatUnsub();
    sellerChatUnsub = null;
  }
}

function syncEnquireDoneMessageBtn(): void {
  const btn = $('#enquire-done-message') as HTMLButtonElement | null;
  if (!btn) return;
  btn.hidden = !state.chatFactoryName;
}

function renderMessageBubbles(host: HTMLElement, messages: ChatMessage[], mineId: string | undefined): void {
  if (!messages.length) {
    host.innerHTML = `<p class="chat-day-sep">No messages yet — say hello</p>`;
    return;
  }
  let lastDay = '';
  const parts: string[] = [];
  for (const m of messages) {
    const day = formatDate(m.createdAt).split(',')[0] || '';
    if (day && day !== lastDay) {
      lastDay = day;
      parts.push(`<div class="chat-day-sep">${escapeHtml(day)}</div>`);
    }
    const mine = Boolean(mineId && m.senderId === mineId);
    parts.push(`
      <div class="chat-bubble ${mine ? 'mine' : 'theirs'}">
        <div class="chat-bubble-body">${escapeHtml(m.body)}</div>
        <span class="chat-bubble-meta">${escapeHtml(formatDate(m.createdAt))}</span>
      </div>`);
  }
  host.innerHTML = parts.join('');
  host.scrollTop = host.scrollHeight;
}

function threadItemHtml(c: Conversation, activeId: string | null): string {
  const title = c.productName || c.factoryName;
  const preview = c.lastBody || 'No messages yet';
  const when = formatDate(c.lastAt || c.updatedAt);
  const unread =
    c.unreadCount && c.unreadCount > 0
      ? `<span class="thread-unread">${c.unreadCount > 99 ? '99+' : c.unreadCount}</span>`
      : '';
  return `
    <button type="button" class="thread-item ${c.id === activeId ? 'active' : ''}" data-open-conversation="${escapeAttr(c.id)}">
      <p class="thread-item-title">${escapeHtml(title)}</p>
      <p class="thread-item-preview">${escapeHtml(preview)}</p>
      <div class="thread-item-meta">
        <span>${escapeHtml(c.factoryName)}${c.productName ? ' · ' + escapeHtml(c.productName) : ''}</span>
        <span>${escapeHtml(when)} ${unread}</span>
      </div>
    </button>`;
}

async function openMessageSeller(opts: {
  factoryName: string;
  productId?: string | null;
  productName?: string | null;
}): Promise<void> {
  state.chatFactoryName = opts.factoryName;
  state.chatProductId = opts.productId || null;
  state.chatProductName = opts.productName || null;

  if (!isSignedIn()) {
    state.authReturn = 'chat';
    showView('auth');
    toast('Sign in to message the seller');
    return;
  }

  // Factory users messaging their own factory: still allow, but nudge.
  if (
    authState.profile?.role === 'factory' &&
    sellerFactoryName() &&
    sellerFactoryName() === opts.factoryName.trim()
  ) {
    toast('This is your factory — open Seller Desk → Messages to reply to buyers');
    state.role = 'seller';
    state.sellerTab = 'messages';
    syncRoleUI();
    showView('seller');
    return;
  }

  try {
    const conv = await getOrCreateConversation({
      factoryName: opts.factoryName,
      productId: opts.productId,
      productName: opts.productName,
    });
    state.conversationId = conv.id;
    showView('chat');
  } catch (err) {
    const msg = err instanceof Error ? err.message : 'Could not open chat';
    toast(msg);
  }
}

async function renderChat(): Promise<void> {
  const gate = $('#chat-auth-gate');
  const shell = $('#chat-shell');
  const picker = $('#chat-thread-picker');
  const back = $('#chat-back');

  if (back) {
    back.onclick = () => {
      if (state.chatProductId) {
        state.productId = state.chatProductId;
        showView('detail');
      } else {
        showView('home');
      }
    };
  }

  if (!isSignedIn()) {
    if (gate) gate.hidden = false;
    if (shell) shell.hidden = true;
    if (picker) picker.hidden = true;
    return;
  }
  if (gate) gate.hidden = true;

  // If no active conversation, show buyer thread list
  if (!state.conversationId) {
    if (shell) shell.hidden = true;
    if (picker) picker.hidden = false;
    const list = await listConversationsForCurrentUser();
    const host = $('#buyer-msg-list');
    const empty = $('#buyer-msg-empty');
    if (host) {
      host.innerHTML = list.map((c) => threadItemHtml(c, null)).join('');
    }
    if (empty) empty.hidden = list.length > 0;
    return;
  }

  if (picker) picker.hidden = true;
  if (shell) shell.hidden = false;

  const conv =
    (await getConversationById(state.conversationId)) ||
    ({
      id: state.conversationId,
      factoryName: state.chatFactoryName || 'Seller',
      productName: state.chatProductName,
      buyerId: authState.user?.id || '',
      createdAt: '',
      updatedAt: '',
    } as Conversation);

  state.chatFactoryName = conv.factoryName;
  if (conv.productId) state.chatProductId = conv.productId;
  if (conv.productName) state.chatProductName = conv.productName;

  const title = $('#chat-title');
  const sub = $('#chat-sub');
  const hint = $('#chat-sync-hint');
  if (title) title.textContent = conv.factoryName;
  if (sub) {
    sub.textContent = conv.productName
      ? `About ${conv.productName}`
      : 'Ask about MOQ, lead time, or specs before you pay';
  }
  if (hint) {
    if (!messagingAvailable()) {
      hint.hidden = false;
      hint.textContent =
        'Supabase messaging unavailable — messages stay in this browser until migration 008 is applied.';
    } else {
      hint.hidden = true;
      hint.textContent = '';
    }
  }

  stopChatSubscription();
  const host = $('#chat-messages');
  if (!host) return;
  chatUnsub = subscribeToConversation(state.conversationId, (msgs) => {
    renderMessageBubbles(host, msgs, authState.user?.id);
    void markConversationRead(state.conversationId!);
  });
}

async function renderSellerMessages(): Promise<void> {
  const list = await listConversationsForCurrentUser();
  const badge = $('#msg-count-badge');
  const unreadTotal = list.reduce((n, c) => n + (c.unreadCount || 0), 0);
  if (badge) {
    badge.hidden = unreadTotal === 0;
    badge.textContent = String(unreadTotal > 99 ? '99+' : unreadTotal);
  }

  const host = $('#seller-msg-list');
  const empty = $('#seller-msg-empty');
  if (host) {
    host.innerHTML = list.map((c) => threadItemHtml(c, state.conversationId)).join('');
  }
  if (empty) empty.hidden = list.length > 0;

  const split = $('#seller-chat-split');
  const pane = $('#seller-chat-pane');
  if (!state.conversationId) {
    if (pane) pane.hidden = true;
    if (split) split.classList.remove('is-thread-open');
    stopSellerChatSubscription();
    return;
  }

  const conv = list.find((c) => c.id === state.conversationId) || (await getConversationById(state.conversationId));
  if (pane) pane.hidden = false;
  if (split) split.classList.add('is-thread-open');
  const title = $('#seller-chat-title');
  const sub = $('#seller-chat-sub');
  if (title) title.textContent = conv?.productName || 'Buyer chat';
  if (sub) {
    const buyerBit = conv?.buyerId ? `Buyer ${conv.buyerId.slice(0, 8)}…` : 'Buyer';
    sub.textContent = conv?.productName ? `${buyerBit} · ${conv.productName}` : buyerBit;
  }

  stopSellerChatSubscription();
  const msgHost = $('#seller-chat-messages');
  if (!msgHost) return;
  sellerChatUnsub = subscribeToConversation(state.conversationId, (msgs) => {
    renderMessageBubbles(msgHost, msgs, authState.user?.id);
    void markConversationRead(state.conversationId!);
  });
}

async function openSellerConversation(id: string): Promise<void> {
  state.conversationId = id;
  state.sellerTab = 'messages';
  await renderSellerMessages();
}


function bindEvents(): void {
  document.body.addEventListener('click', (e) => {
    const clickTarget = eventEl(e);
    if (!clickTarget) return;
    const nav = clickTarget.closest('[data-nav]') as HTMLElement | null;
    if (nav) {
      e.preventDefault();
      const target = nav.dataset.nav;
      if (target === 'seller') {
        state.role = 'seller';
        syncRoleUI();
      }
      if (target === 'checkout') {
        state.guestCheckout = false;
      }
      if (target === 'auth' && nav.dataset.authReturn) {
        state.authReturn = nav.dataset.authReturn || null;
      }
      if (target === 'detail') return;
      showView(target);
      return;
    }

    const card = clickTarget.closest('[data-product-id]') as HTMLElement | null;
    if (card && state.view === 'home') {
      state.productId = card.dataset.productId || null;
      showView('detail');
      return;
    }

    const pill = clickTarget.closest('[data-category]') as HTMLElement | null;
    if (pill) {
      state.category = pill.dataset.category || 'All';
      renderHome();
      return;
    }

    const remove = clickTarget.closest('[data-remove]') as HTMLElement | null;
    if (remove) {
      setCart(getCart().filter((i) => i.productId !== remove.dataset.remove));
      renderCart();
      updateBadges();
      toast('Removed from cart');
      return;
    }

    const stepBtn = clickTarget.closest('[data-qty-step]') as HTMLElement | null;
    if (stepBtn) {
      const id = stepBtn.dataset.qtyId;
      const delta = parseInt(stepBtn.dataset.qtyStep || '0', 10);
      if (!id || !delta) return;
      const cart = getCart();
      const item = cart.find((i) => i.productId === id);
      const p = productById(id);
      if (!item || !p) return;
      const next = Math.max(p.moq, item.qty + delta);
      setCartQty(id, next);
      return;
    }

    const sellerTab = clickTarget.closest('[data-seller-tab]') as HTMLElement | null;
    if (sellerTab) {
      state.sellerTab = sellerTab.dataset.sellerTab || 'enquiries';
      renderSeller();
      return;
    }

    const catEdit = clickTarget.closest('[data-catalogue-edit]') as HTMLElement | null;
    if (catEdit) {
      const id = catEdit.dataset.catalogueEdit;
      const product = productById(id);
      if (product) openCatalogueForm(product);
      return;
    }

    const openConv = clickTarget.closest('[data-open-conversation]') as HTMLElement | null;
    if (openConv) {
      const id = openConv.dataset.openConversation;
      if (!id) return;
      if (state.view === 'seller' || state.role === 'seller') {
        void openSellerConversation(id);
      } else {
        state.conversationId = id;
        showView('chat');
      }
      return;
    }

    const authTab = clickTarget.closest('[data-auth-tab]') as HTMLElement | null;
    if (authTab) {
      state.authTab = authTab.dataset.authTab || 'signin';
      renderAuth();
      return;
    }

    const authNav = clickTarget.closest('[data-nav="auth"]') as HTMLElement | null;
    if (authNav && authNav.dataset.authReturn) {
      state.authReturn = authNav.dataset.authReturn || null;
    }

    const roleBtn = clickTarget.closest('[data-role]') as HTMLElement | null;
    if (roleBtn) {
      state.role = roleBtn.dataset.role === 'seller' ? 'seller' : 'buyer';
      syncRoleUI();
      showView(state.role === 'seller' ? 'seller' : 'home');
    }
  });

  document.body.addEventListener('change', (e) => {
    const changeTarget = eventEl(e);
    if (!changeTarget) return;
    const qtyInput = changeTarget.closest('[data-qty]') as HTMLInputElement | null;
    if (qtyInput) {
      const id = qtyInput.dataset.qty;
      if (!id) return;
      const p = productById(id);
      let qty = Math.max(1, parseInt(qtyInput.value, 10) || 1);
      if (p && qty < p.moq) {
        toast(`MOQ is ${p.moq.toLocaleString('en-GB')} ${p.unit}`);
      }
      setCartQty(id, qty);
    }
  });

  $<HTMLInputElement>('#search-input').addEventListener('input', (e) => {
    state.search = (e.target as HTMLInputElement).value;
    renderHome();
  });
  $<HTMLSelectElement>('#sort-select').addEventListener('change', (e) => {
    state.sort = (e.target as HTMLSelectElement).value;
    renderHome();
  });

  $('#menu-toggle').addEventListener('click', () => {
    const open = $('#mobile-drawer').hidden;
    if (open) openDrawer();
    else closeDrawer();
  });
  $('#drawer-overlay').addEventListener('click', closeDrawer);
  $('#drawer-close')?.addEventListener('click', closeDrawer);
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && !$('#mobile-drawer')?.hidden) closeDrawer();
  });

  initTheme();
  const themeSelect = $<HTMLSelectElement>('#theme-select');
  if (themeSelect) {
    themeSelect.value = getTheme();
    themeSelect.addEventListener('change', () => {
      setTheme(themeSelect.value);
      toast(`Theme: ${themeSelect.value === 'ink' ? 'Ink' : 'Lemon'}`);
    });
  }

  const currencySelect = $<HTMLSelectElement>('#currency-select');
  if (currencySelect) {
    currencySelect.value = getCurrency();
    currencySelect.addEventListener('change', () => {
      setCurrency(currencySelect.value);
      refreshPrices();
      toast(`Currency: ${currencySelect.value}`);
    });
  }

  $<HTMLFormElement>('#enquire-form').addEventListener('submit', async (e) => {
    e.preventDefault();
    const p = productById($<HTMLInputElement>('#enquire-product-id').value);
    if (!p) return;
    const company = $<HTMLInputElement>('#enq-company').value.trim();
    const contact = $<HTMLInputElement>('#enq-contact').value.trim();
    const email = $<HTMLInputElement>('#enq-email').value.trim();
    const phone = $<HTMLInputElement>('#enq-phone').value.trim();
    const quantity = parseInt($<HTMLInputElement>('#enq-qty').value, 10);
    const message = $<HTMLTextAreaElement>('#enq-message').value.trim();
    if (!company || !contact || !email || !message || !quantity || quantity < 1) {
      toast('Please complete all required fields');
      return;
    }
    const saved = await saveEnquiry({
      productId: p.id,
      productName: p.name,
      factoryName: p.factory,
      company,
      contact,
      email,
      phone,
      quantity,
      message,
    });
    $('#enquire-done-body').textContent = `Thanks, ${contact}. Your enquiry about “${p.name}” (${quantity.toLocaleString()} ${p.unit}) was saved. Reference ${saved.id.slice(0, 8)}. The factory can follow up at ${email}.`;
    state.chatFactoryName = p.factory;
    state.chatProductId = p.id;
    state.chatProductName = p.name;
    (e.target as HTMLFormElement).reset();
    showView('enquire-done');
  });

  $<HTMLFormElement>('#checkout-form').addEventListener('submit', async (e) => {
    e.preventDefault();
    if (checkoutBusy) return;
    const cart = getCart();
    if (!cart.length) {
      showView('cart');
      return;
    }

    clearFieldErrors('co-', ['company', 'contact', 'email', 'phone', 'address']);
    const company = $<HTMLInputElement>('#co-company').value.trim();
    const contact = $<HTMLInputElement>('#co-contact').value.trim();
    const email = $<HTMLInputElement>('#co-email').value.trim();
    const phone = $<HTMLInputElement>('#co-phone').value.trim();
    const address = $<HTMLTextAreaElement>('#co-address').value.trim();
    const notes = $<HTMLTextAreaElement>('#co-notes').value.trim();

    let invalid = false;
    if (!company || company.length < 2) {
      setFieldError('co-company', 'Enter your company name');
      invalid = true;
    }
    if (!contact || contact.length < 2) {
      setFieldError('co-contact', 'Enter a contact name');
      invalid = true;
    }
    if (!email || !isValidEmail(email)) {
      setFieldError('co-email', 'Enter a valid email address');
      invalid = true;
    }
    if (phone && !isValidPhone(phone)) {
      setFieldError('co-phone', 'Enter a valid phone (7–15 digits) or leave blank');
      invalid = true;
    }
    if (!address || address.length < 10) {
      setFieldError('co-address', 'Enter a full ship-to address (city and state)');
      invalid = true;
    }
    if (invalid) {
      const formErr = $('#checkout-form-error');
      if (formErr) {
        formErr.textContent = 'Please fix the highlighted fields before placing your order.';
        formErr.hidden = false;
      }
      toast('Please complete all required checkout fields');
      return;
    }

    const items = [];
    let merchandise = 0;
    for (const line of cart) {
      const p = productById(line.productId);
      if (!p) continue;
      if (line.qty < p.moq) {
        toast(`“${p.name}” is below MOQ`);
        showView('cart');
        return;
      }
      const unitPrice = priceForQtyNgn(p, line.qty);
      const lineTotal = unitPrice * line.qty;
      merchandise += lineTotal;
      items.push({
        productId: p.id,
        name: p.name,
        factory: p.factory,
        sku: p.sku,
        unitPrice,
        qty: line.qty,
        lineTotal,
      });
    }
    if (!items.length) {
      toast('Your cart has no valid products');
      showView('cart');
      return;
    }

    const freight = FREIGHT_FLAT;
    const total = merchandise + freight;
    checkoutBusy = true;
    const submitBtn = $('#checkout-submit') as HTMLButtonElement | null;
    if (submitBtn) {
      submitBtn.disabled = true;
      submitBtn.textContent = 'Placing order…';
    }

    try {
      const order = await saveOrder({
        company,
        contact,
        email,
        phone,
        address,
        notes,
        merchandise,
        freight,
        total,
        currency: 'NGN',
        items,
        paymentStatus: 'pending',
        paymentMode: isPaystackConfigured() ? 'paystack' : 'demo',
      });
      lastOrderId = order.id;
      (e.target as HTMLFormElement).reset();
      updateBadges();

      if (isPaystackConfigured()) {
        toast('Order saved — opening Paystack…');
        const result = await chargeWithPaystack({
          email,
          amountNgn: total,
          reference: order.id,
          metadata: { order_id: order.id, company },
        });
        if (result.ok) {
          await updateOrderPayment(order.id, {
            paymentStatus: 'paid',
            paymentMode: 'paystack',
            paymentRef: result.reference,
            paidAt: new Date().toISOString(),
            status: 'confirmed',
          });
          toast('Payment successful');
        } else if (result.cancelled) {
          await updateOrderPayment(order.id, {
            paymentStatus: 'pending',
            paymentMode: 'paystack',
            status: 'placed',
          });
          toast('Payment cancelled — you can pay from the confirmation screen');
        } else {
          await updateOrderPayment(order.id, {
            paymentStatus: 'pending',
            paymentMode: 'paystack',
          });
          toast(result.message || 'Payment not completed');
        }
      } else {
        toast('Order placed — complete demo payment next');
      }

      showView('order-done');
    } catch (err) {
      const formErr = $('#checkout-form-error');
      const msg = err instanceof Error ? err.message : 'Could not place order';
      if (formErr) {
        formErr.textContent = msg;
        formErr.hidden = false;
      }
      toast(msg);
    } finally {
      checkoutBusy = false;
      if (submitBtn) {
        submitBtn.disabled = false;
        submitBtn.textContent = isPaystackConfigured() ? 'Pay & place order' : 'Place order (demo pay next)';
      }
    }
  });

  $('#order-pay-now')?.addEventListener('click', async () => {
    const order = lastOrderId ? getLocalOrderById(lastOrderId) : null;
    if (!order || order.paymentStatus === 'paid') return;
    const result = await chargeWithPaystack({
      email: order.email,
      amountNgn: order.total,
      reference: order.id,
      metadata: { order_id: order.id },
    });
    if (result.ok) {
      await updateOrderPayment(order.id, {
        paymentStatus: 'paid',
        paymentMode: 'paystack',
        paymentRef: result.reference,
        paidAt: new Date().toISOString(),
        status: 'confirmed',
      });
      toast('Payment successful');
      renderOrderDone();
    } else if (result.cancelled) {
      toast('Payment cancelled');
    } else {
      toast(result.message || 'Payment failed');
    }
  });

  $('#order-demo-pay')?.addEventListener('click', async () => {
    const order = lastOrderId ? getLocalOrderById(lastOrderId) : null;
    if (!order || order.paymentStatus === 'paid') return;
    const result = completeDemoPayment(order.id);
    await updateOrderPayment(order.id, {
      paymentStatus: 'paid',
      paymentMode: 'demo',
      paymentRef: result.reference,
      paidAt: new Date().toISOString(),
      status: 'confirmed',
    });
    toast('Demo payment recorded');
    renderOrderDone();
  });

  const enquireGuest = $('#enquire-continue-guest');
  if (enquireGuest) {
    enquireGuest.addEventListener('click', () => {
      state.guestEnquire = true;
      syncEnquireGate();
    });
  }
  const checkoutGuest = $('#checkout-continue-guest');
  if (checkoutGuest) {
    checkoutGuest.addEventListener('click', () => {
      state.guestCheckout = true;
      syncCheckoutGate();
    });
  }

  $('#signin-form')?.addEventListener('submit', async (e) => {
    e.preventDefault();
    const errEl = $('#signin-error');
    errEl.hidden = true;
    try {
      await signIn({
        email: $<HTMLInputElement>('#si-email').value.trim(),
        password: $<HTMLInputElement>('#si-password').value,
      });
      toast('Signed in');
      afterAuthSuccess();
    } catch (err) {
      errEl.textContent = err instanceof Error ? err.message : 'Sign in failed';
      errEl.hidden = false;
    }
  });

  $('#signup-form')?.addEventListener('submit', async (e) => {
    e.preventDefault();
    const errEl = $('#signup-error');
    errEl.hidden = true;
    const roleInput = document.querySelector('input[name="su-role"]:checked') as HTMLInputElement | null;
    try {
      const result = await signUp({
        email: $<HTMLInputElement>('#su-email').value.trim(),
        password: $<HTMLInputElement>('#su-password').value,
        role: roleInput?.value === 'factory' ? 'factory' : 'buyer',
        company: $<HTMLInputElement>('#su-company').value.trim(),
      });
      // Only proceed when Supabase returned a confirmed session (Confirm email off,
      // or already confirmed). Pending / unconfirmed never calls afterAuthSuccess.
      if (result.session && !result.pendingConfirmation) {
        toast('Account created');
        afterAuthSuccess();
      } else {
        toast('Check your email to confirm, then sign in');
        state.authTab = 'signin';
        state.authReturn = null;
        renderAuth();
        updateAuthChip();
      }
    } catch (err) {
      errEl.textContent = err instanceof Error ? err.message : 'Sign up failed';
      errEl.hidden = false;
    }
  });

  $('#btn-sign-out')?.addEventListener('click', async () => {
    try {
      await signOut();
      toast('Signed out');
      renderAuth();
      updateAuthChip();
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Sign out failed');
    }
  });

  $('#catalogue-add-btn')?.addEventListener('click', () => {
    state.sellerTab = 'catalogue';
    openCatalogueForm(null);
  });

  $('#catalogue-cancel-btn')?.addEventListener('click', () => {
    resetCatalogueForm();
  });

  $('#enquire-done-message')?.addEventListener('click', () => {
    if (!state.chatFactoryName) return;
    void openMessageSeller({
      factoryName: state.chatFactoryName,
      productId: state.chatProductId,
      productName: state.chatProductName,
    });
  });

  $('#seller-chat-back-list')?.addEventListener('click', () => {
    state.conversationId = null;
    stopSellerChatSubscription();
    void renderSellerMessages();
  });

  const wireComposer = (
    formSel: string,
    inputSel: string,
    getConversationId: () => string | null,
    afterSend: () => void,
  ) => {
    const form = $<HTMLFormElement>(formSel);
    if (!form) return;
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      if (chatSendBusy) return;
      const cid = getConversationId();
      if (!cid) {
        toast('Open a conversation first');
        return;
      }
      const input = $<HTMLTextAreaElement>(inputSel);
      const body = input?.value?.trim() || '';
      if (!body) return;
      chatSendBusy = true;
      try {
        await sendMessage(cid, body);
        if (input) input.value = '';
        afterSend();
      } catch (err) {
        toast(err instanceof Error ? err.message : 'Could not send');
      } finally {
        chatSendBusy = false;
      }
    });
    const input = $<HTMLTextAreaElement>(inputSel);
    input?.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        form.requestSubmit();
      }
    });
  };

  wireComposer('#chat-form', '#chat-input', () => state.conversationId, () => {
    /* realtime/poll refreshes bubbles */
  });
  wireComposer('#seller-chat-form', '#seller-chat-input', () => state.conversationId, () => {
    void renderSellerMessages();
  });

  $('#cat-image')?.addEventListener('change', () => {
    const input = $<HTMLInputElement>('#cat-image');
    const file = input?.files?.[0] || null;
    cataloguePendingFile = file;
    const preview = $('#cat-image-preview');
    if (!preview) return;
    if (!file) {
      preview.hidden = true;
      preview.innerHTML = '';
      return;
    }
    const url = URL.createObjectURL(file);
    preview.hidden = false;
    preview.innerHTML = `<img src="${escapeAttr(url)}" alt="" />`;
  });

  $('#catalogue-form')?.addEventListener('submit', async (e) => {
    e.preventDefault();
    if (catalogueBusy) return;
    const errEl = $('#catalogue-form-error');
    if (errEl) {
      errEl.hidden = true;
      errEl.textContent = '';
    }
    const name = $<HTMLInputElement>('#cat-name').value.trim();
    const moq = parseInt($<HTMLInputElement>('#cat-moq').value, 10);
    const unitPriceNgn = parseFloat($<HTMLInputElement>('#cat-price').value);
    const category = $<HTMLSelectElement>('#cat-category').value;
    const description = $<HTMLTextAreaElement>('#cat-desc').value.trim();
    const active = $<HTMLInputElement>('#cat-active').checked;
    const id = $<HTMLInputElement>('#cat-id').value.trim() || undefined;
    if (!name || name.length < 2) {
      if (errEl) {
        errEl.textContent = 'Enter a product name';
        errEl.hidden = false;
      }
      toast('Enter a product name');
      return;
    }
    if (!moq || moq < 1) {
      if (errEl) {
        errEl.textContent = 'MOQ must be at least 1';
        errEl.hidden = false;
      }
      toast('MOQ must be at least 1');
      return;
    }
    if (!Number.isFinite(unitPriceNgn) || unitPriceNgn < 0) {
      if (errEl) {
        errEl.textContent = 'Enter a unit price in NGN';
        errEl.hidden = false;
      }
      toast('Enter a unit price in NGN');
      return;
    }
    if (!sellerFactoryName()) {
      if (errEl) {
        errEl.textContent =
          'Sign up / sign in as Factory and set your company name so products belong to your factory.';
        errEl.hidden = false;
      }
      toast('Factory company required');
      return;
    }

    const existing = id ? productById(id) : undefined;
    catalogueBusy = true;
    const saveBtn = $('#catalogue-save-btn') as HTMLButtonElement | null;
    if (saveBtn) {
      saveBtn.disabled = true;
      saveBtn.textContent = 'Saving…';
    }
    try {
      const saved = await saveSellerProduct(
        {
          id,
          name,
          moq,
          unitPriceNgn: Math.round(unitPriceNgn),
          category,
          description,
          active,
          imageUrl: existing?.imageUrl ?? null,
        },
        cataloguePendingFile,
      );
      const warning = (saved as CatalogProduct & { syncWarning?: string }).syncWarning;
      const syncHint = $('#catalogue-sync-hint');
      if (syncHint) {
        if (warning) {
          syncHint.textContent = warning;
          syncHint.hidden = false;
        } else {
          syncHint.hidden = true;
          syncHint.textContent = '';
        }
      }
      toast(warning ? 'Product saved locally' : id ? 'Product updated' : 'Product added');
      resetCatalogueForm();
      homeRenderKey = '';
      state.sellerTab = 'catalogue';
      await renderSeller();
      if (state.view === 'home') renderHome();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Could not save product';
      if (errEl) {
        errEl.textContent = msg;
        errEl.hidden = false;
      }
      toast(msg);
    } finally {
      catalogueBusy = false;
      if (saveBtn) {
        saveBtn.disabled = false;
        saveBtn.textContent = 'Save product';
      }
    }
  });
}

function syncRoleUI(): void {
  $$('.role-btn').forEach((b) => b.classList.toggle('active', b.dataset.role === state.role));
}

/** Reload cart/orders/seller UI after storage namespace switches on auth change. */
function reloadDeskFromStorage(): void {
  updateBadges();
  if (state.view === 'cart') renderCart();
  if (state.view === 'orders') renderBuyerOrders();
  if (state.view === 'seller') renderSeller();
  if (state.view === 'checkout') renderCheckout();
  if (state.view === 'enquire') syncEnquireGate();
  if (state.view === 'chat') void renderChat();
}

function handleAuthStorageSwitch(): void {
  const { changed } = syncStorageScopeFromAuth();
  if (!changed) return;
  // Isolate: do not carry guest/other-user cart into the new account.
  state.guestEnquire = false;
  state.guestCheckout = false;
  reloadDeskFromStorage();
}

async function init(): Promise<void> {
  bindEvents();
  syncRoleUI();
  syncCurrencySelect();
  onCatalogChange(() => {
    homeRenderKey = '';
    if (state.view === 'home') renderHome();
    if (state.view === 'detail') renderDetail();
    if (state.view === 'cart') renderCart();
  });
  // Register before initAuth so the first session notify is not missed.
  onAuthChange(() => {
    handleAuthStorageSwitch();
    updateAuthChip();
    if (state.view === 'auth') renderAuth();
    if (state.view === 'enquire') syncEnquireGate();
    if (state.view === 'checkout') syncCheckoutGate();
    void refreshCatalog({ includeInactiveForFactory: sellerFactoryName() });
    if (isSignedIn() && authState.profile?.role === 'factory' && state.role !== 'seller') {
      /* do not force role switch mid-browse */
    }
  });
  await initAuth();
  // Ensure scope matches session even if no listener fired yet.
  syncStorageScopeFromAuth();
  await refreshCatalog({ includeInactiveForFactory: sellerFactoryName() });
  updateBadges();
  updateAuthChip();
  showView('home');
  console.info(`[THEG MARKET PLACE] Backend: ${backendLabel()}`);
}

init();
